import React, { useEffect, useRef, useState } from 'react';
import ExcelExportButton from './ExcelExportButton';
import SrCategorySelect, { ALL_SR_CATEGORIES, SrCategoryOption } from './SrCategorySelect';
import { chosen, ExportCell, ExportSheet, reportFileName, reportMeta } from '../helpers/TableExcelExport';
import { Card, Form } from 'react-bootstrap-v5';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper';
import { numberFormat } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface ProjectOption {
	label: string;
	value: string | number;
}

interface SkuRow {
	name: string;
	achievedLtr: number;
	amount: number;
}

interface SellerRow {
	name: string;
	target: number | null;
	achievedTarget: number;
	achievedLtrPercent: number | null;
}

interface DetailRow {
	name: string;
	targetLiter: number;
	achievedLtr: number;
	amount: number;
	achievedLtrPercent: number | null;
}

interface PlaceFigure {
	name: string;
	quantity: number;
	unit: string;
}

interface MisDashboardData {
	title: string;
	periodLabel: string;
	totalSales: {
		totalTarget: number;
		achievedTarget: number;
		totalAmount: number;
		achievedPercent: number | null;
	};
	oldCustomer: number;
	newCustomer: number;
	mostSellingItem: PlaceFigure;
	numberOfInvoice: number;
	bestCustomer: {
		name: string;
		value: number;
		location: string;
		unit: string;
	};
	metrics: {
		totalTarget: number;
		achievedTarget: number;
		achievedTargetPercent: number | null;
		multigradeTarget: number;
		multigradeAchieved: number;
		multigradeAchievedPercent: number | null;
		mcoBestDistrict: PlaceFigure;
		cngBestDistrict: PlaceFigure;
	};
	skuList: SkuRow[];
	bestDivisionForMco: PlaceFigure;
	bestSellers: SellerRow[];
	details: DetailRow[];
	totalFreeProducts: number;
	numberOfCustomersTrans: number;
}

const getDefaultMonthRange = () => {
	const now = new Date();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const currentMonth = `${now.getFullYear()}-${month}`;

	return {
		fromDate: currentMonth,
		toDate: currentMonth,
	};
};

/** Litres and counts as on the sheet: thousands separated, decimals only when there are some. */
const qty = (value: number | null | undefined) => (value === null || value === undefined ? '-' : Number(value).toLocaleString('en-US', { maximumFractionDigits: 2 }));
const percent = (value: number | null | undefined) => (value === null || value === undefined ? '-' : qty(value));
const withPercent = (value: number | null | undefined) => (value === null || value === undefined ? '-' : `${qty(value)}%`);

const Tile: React.FC<{ title?: React.ReactNode; className?: string; dark?: boolean; light?: boolean }> = ({ title, className = '', dark, light, children }) => (
	<div className={`mis-dash-tile ${dark ? 'mis-dash-dark' : ''} ${light ? 'mis-dash-light' : ''} ${className}`}>
		{title && <div className="mis-dash-tile-head mis-dash-head">{title}</div>}
		<div className="mis-dash-tile-body">{children}</div>
	</div>
);

const MisDashboardReport: React.FC = () => {
	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const defaultRange = getDefaultMonthRange();

	const [loading, setLoading] = useState(false);
	const [reportData, setReportData] = useState<MisDashboardData | null>(null);
	const reportRef = useRef<HTMLDivElement>(null);
	const [projects, setProjects] = useState<ProjectOption[]>([]);
	const [project, setProject] = useState<ProjectOption>({ label: 'Select Project', value: '' });
	const [category, setCategory] = useState<SrCategoryOption>(query.get('idSrCategories') ? { label: '', value: String(query.get('idSrCategories')) } : ALL_SR_CATEGORIES);
	const [formData, setFormData] = useState({
		idProjects: query.get('idProjects') ?? '',
		idSrCategories: query.get('idSrCategories') ?? '',
		fromDate: query.get('fromDate') ?? defaultRange.fromDate,
		toDate: query.get('toDate') ?? defaultRange.toDate,
	});

	useEffect(() => {
		getProjects();
	}, []);

	useEffect(() => {
		if (projects.length > 0) {
			const projectObj = Number(query.get('idProjects'))
				? projects.find((item) => String(item.value) === String(query.get('idProjects')))
				: projects[0];

			setProject({
				label: projectObj?.label ?? 'Select Project',
				value: projectObj?.value ?? '',
			});
			setFormData((prev) => ({
				...prev,
				idProjects: projectObj ? String(projectObj.value) : '',
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projects]);

	const getProjects = () => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const projectOptions = (resp.data || []).map((item: any) => ({
					label: item.name,
					value: item.id,
				}));
				setProjects(projectOptions);
			})
			.catch((error) => console.error('Error fetching projects:', error));
	};

	const handleProjectChange = (selected: any) => {
		setProject(selected || { label: 'Select Project', value: '' });
		setFormData((prev) => ({
			...prev,
			idProjects: selected ? String(selected.value) : '',
		}));
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleViewReport = async () => {
		if (!formData.idProjects) {
			toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
			return;
		}
		if (!formData.fromDate || !formData.toDate) {
			toast.error('Please select both From Month and To Month', { position: 'bottom-right', autoClose: 4000 });
			return;
		}
		if (formData.fromDate > formData.toDate) {
			toast.error('To Month must be after or equal to From Month', { position: 'bottom-right', autoClose: 4000 });
			return;
		}

		setLoading(true);
		try {
			const response = await fetch(process.env.REACT_APP_API_URL + 'mis-reports/dashboard', postRequestOptions(formData));
			const data = await response.json();
			if (data.success) {
				setReportData(data.data);
			} else {
				setReportData(null);
				toast.error(data.message ?? 'Failed to load report', { position: 'bottom-right', autoClose: 4000 });
			}
		} catch (error) {
			console.error('Error loading dashboard:', error);
			setReportData(null);
			toast.error('Failed to load report', { position: 'bottom-right', autoClose: 4000 });
		} finally {
			setLoading(false);
		}
	};

	const skuTotalAchieved = reportData?.skuList.reduce((sum, row) => sum + row.achievedLtr, 0) ?? 0;
	const skuTotalAmount = reportData?.skuList.reduce((sum, row) => sum + row.amount, 0) ?? 0;
	const detailsTotal = {
		target: reportData?.details.reduce((sum, row) => sum + row.targetLiter, 0) ?? 0,
		achieved: reportData?.details.reduce((sum, row) => sum + row.achievedLtr, 0) ?? 0,
		amount: reportData?.details.reduce((sum, row) => sum + row.amount, 0) ?? 0,
	};
	const heading = reportData ? `${reportData.title}${category.value ? ' (' + category.label + ')' : ''} ${reportData.periodLabel}` : '';

	// The dashboard's boxes (figures outside the tables) as one sheet: section, item, value.
	const summarySheet = (): ExportSheet[] => {
		if (!reportData) {
			return [];
		}
		const withUnit = (label: string, unit?: string) => (unit ? `${label} (${unit})` : label);
		const lines: Array<[string, string, string]> = [
			['Total Sales', 'Total Target', qty(reportData.totalSales.totalTarget)],
			['Total Sales', 'Achieved Target', qty(reportData.totalSales.achievedTarget)],
			['Total Sales', 'Total Amount', numberFormat(reportData.totalSales.totalAmount)],
			['Total Sales', 'Achieved %', percent(reportData.totalSales.achievedPercent)],
			['Customers', 'Old Customer', qty(reportData.oldCustomer)],
			['Customers', 'New Customer', qty(reportData.newCustomer)],
			['Most Selling Items', withUnit(reportData.mostSellingItem.name, reportData.mostSellingItem.unit), qty(reportData.mostSellingItem.quantity)],
			['Number of Invoice', 'Invoices (Pcs)', qty(reportData.numberOfInvoice)],
			['Best Customer', withUnit(`${reportData.bestCustomer.name}${reportData.bestCustomer.location ? ' - ' + reportData.bestCustomer.location : ''}`, reportData.bestCustomer.unit), qty(reportData.bestCustomer.value)],
			['Target', 'Total Target', qty(reportData.metrics.totalTarget)],
			['Target', 'Achieved Target', qty(reportData.metrics.achievedTarget)],
			['Target', 'Achieved %', percent(reportData.metrics.achievedTargetPercent)],
			['Multigrade', 'Multigrade Target', qty(reportData.metrics.multigradeTarget)],
			['Multigrade', 'Achieved Target', qty(reportData.metrics.multigradeAchieved)],
			['Multigrade', 'Achieved %', percent(reportData.metrics.multigradeAchievedPercent)],
			['MCO Best District', withUnit(reportData.metrics.mcoBestDistrict.name, reportData.metrics.mcoBestDistrict.unit), qty(reportData.metrics.mcoBestDistrict.quantity)],
			['CNG Best District', withUnit(reportData.metrics.cngBestDistrict.name, reportData.metrics.cngBestDistrict.unit), qty(reportData.metrics.cngBestDistrict.quantity)],
			['Best Division For MCO', withUnit(reportData.bestDivisionForMco.name, reportData.bestDivisionForMco.unit), qty(reportData.bestDivisionForMco.quantity)],
			['Free Products', 'Total Free Products (Ltr.)', qty(reportData.totalFreeProducts)],
			['Customers', 'Number Of Customers Trans.', qty(reportData.numberOfCustomersTrans)],
		];
		const header: ExportCell[] = [{ v: 'Section', h: 1 }, { v: 'Item', h: 1 }, { v: 'Value', h: 1, a: 'right' }];
		return [{
			name: 'Summary',
			title: 'Summary',
			headerRows: 1,
			rows: [header, ...lines.map(([section, item, value]): ExportCell[] => [{ v: section }, { v: item }, { v: value, a: 'right' }])],
		}];
	};

	return (
		<>
			<Card className="mb-4">
				<Card.Body>
					<div className="row">
						<div className="col-md-3">
							<Form.Group className="mb-3">
								<Form.Label>
									Project<span className="required"></span>
								</Form.Label>
								<Select
									value={project}
									onChange={handleProjectChange}
									options={projects}
									menuPosition="fixed"
									styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
									isClearable={true}
								/>
							</Form.Group>
						</div>
						<div className="col-md-2">
							<Form.Group className="mb-3" controlId="srCategory">
								<Form.Label>SR Category</Form.Label>
								<SrCategorySelect
									idProjects={formData.idProjects}
									value={category}
									onChange={(option) => {
										setCategory(option);
										setFormData((prev) => ({ ...prev, idSrCategories: option.value }));
									}}
								/>
							</Form.Group>
						</div>
						<div className="col-md-2">
							<Form.Group className="mb-3">
								<Form.Label>
									From Month<span className="required"></span>
								</Form.Label>
								<Form.Control
									type="month"
									name="fromDate"
									value={formData.fromDate}
									onChange={handleChange}
								/>
							</Form.Group>
						</div>
						<div className="col-md-2">
							<Form.Group className="mb-3">
								<Form.Label>
									To Month<span className="required"></span>
								</Form.Label>
								<Form.Control
									type="month"
									name="toDate"
									value={formData.toDate}
									onChange={handleChange}
								/>
							</Form.Group>
						</div>
						<div className="col-md-3">
							<div className="mt-8 d-flex">
								<button className="btn btn-sm btn-primary" onClick={handleViewReport} disabled={loading}>
									{loading && <span className="spinner-border spinner-border-sm me-1"></span>}
									<span>
										<i className="fas fa-eye"></i> View Report
									</span>
								</button>
								<ExcelExportButton
									className="btn btn-sm btn-success ms-2"
									targetRef={reportRef}
									title={heading || 'MIS Dashboard'}
									filename={reportFileName('mis-dashboard', formData.fromDate, formData.toDate)}
									meta={reportMeta({ Project: chosen(project), 'SR Category': chosen(category), Period: reportData?.periodLabel })}
									extraSheets={summarySheet}
									disabled={loading || !reportData}
								/>
							</div>
						</div>
					</div>
				</Card.Body>
			</Card>

			{reportData && (
				<Card>
					<Card.Body ref={reportRef} className="mis-dash">
						<div className="mis-dash-title text-primary">{heading}</div>

						{/* Total sales, customers, most selling item, invoices, best customer */}
						<div className="row g-1 mb-1">
							<div className="col-xl-4 col-md-6">
								<Tile title="Total Sales">
									<table className="mis-dash-pairs" data-export-ignore>
										<tbody>
											<tr><td>Total Target</td><td>{qty(reportData.totalSales.totalTarget)}</td></tr>
											<tr><td>Achieved Target</td><td>{qty(reportData.totalSales.achievedTarget)}</td></tr>
											<tr><td>Total Amount</td><td>{numberFormat(reportData.totalSales.totalAmount)}</td></tr>
											<tr><td>Achieved %</td><td>{percent(reportData.totalSales.achievedPercent)}</td></tr>
										</tbody>
									</table>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-6 d-flex flex-column gap-1">
								<Tile title="Old Customer" className="flex-fill">
									<div className="mis-dash-big">{qty(reportData.oldCustomer)}</div>
								</Tile>
								<Tile title="New Customer" className="flex-fill">
									<div className="mis-dash-big">{qty(reportData.newCustomer)}</div>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-4">
								<Tile title="Most Selling Items" light>
									<div className="mis-dash-name">{reportData.mostSellingItem.name}</div>
									<div className="mis-dash-big">{qty(reportData.mostSellingItem.quantity)}</div>
									<div className="mis-dash-unit">{reportData.mostSellingItem.unit}</div>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-4">
								<Tile title="Number of Invoice" dark>
									<div className="mis-dash-big">{qty(reportData.numberOfInvoice)} Pcs</div>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-4">
								<Tile title="Best Customer">
									<div className="mis-dash-name">{reportData.bestCustomer.name}</div>
									<div className="mis-dash-big">{qty(reportData.bestCustomer.value)}</div>
									<div className="mis-dash-name">{reportData.bestCustomer.location}</div>
								</Tile>
							</div>
						</div>

						{/* Targets and best districts */}
						<div className="row g-1 mb-1">
							<div className="col-xl-2 col-md-4 col-6">
								<Tile title="Total Target" light>
									<div className="mis-dash-mid">{qty(reportData.metrics.totalTarget)}</div>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								<Tile title="Achieved Target" light>
									<div className="mis-dash-mid">{qty(reportData.metrics.achievedTarget)}</div>
									<div className="mis-dash-unit">{withPercent(reportData.metrics.achievedTargetPercent)}</div>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								<Tile title="Multigrade Target" light>
									<div className="mis-dash-mid">{qty(reportData.metrics.multigradeTarget)}</div>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								<Tile title="Achieved Target" light>
									<div className="mis-dash-mid">{qty(reportData.metrics.multigradeAchieved)}</div>
									<div className="mis-dash-unit">{withPercent(reportData.metrics.multigradeAchievedPercent)}</div>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								<Tile title="MCO Best District" light>
									<div className="mis-dash-name">{reportData.metrics.mcoBestDistrict.name}</div>
									<div className="mis-dash-mid">{qty(reportData.metrics.mcoBestDistrict.quantity)} {reportData.metrics.mcoBestDistrict.unit}</div>
								</Tile>
							</div>
							<div className="col-xl-2 col-md-4 col-6">
								<Tile title="CNG Best District" light>
									<div className="mis-dash-name">{reportData.metrics.cngBestDistrict.name}</div>
									<div className="mis-dash-mid">{qty(reportData.metrics.cngBestDistrict.quantity)} {reportData.metrics.cngBestDistrict.unit}</div>
								</Tile>
							</div>
						</div>

						{/* SKU list, best division, best sellers */}
						<div className="row g-1 mb-1">
							<div className="col-xl-5">
								<div className="mis-dash-tile" data-export-title="SKU List">
									<table className="mis-dash-table">
										<thead>
											<tr className="mis-dash-head">
												<th>SKU List</th>
												<th className="text-end">Achieved Ltr.</th>
												<th className="text-end">Amount</th>
											</tr>
										</thead>
										<tbody>
											{reportData.skuList.length === 0 ? (
												<tr>
													<td colSpan={3} className="text-center py-5">No SKU data</td>
												</tr>
											) : (
												reportData.skuList.map((row) => (
													<tr key={row.name}>
														<td>{row.name}</td>
														<td className="text-end">{qty(row.achievedLtr)}</td>
														<td className="text-end">{numberFormat(row.amount)}</td>
													</tr>
												))
											)}
										</tbody>
										<tfoot>
											<tr className="fw-bold bg-light-primary text-primary">
												<td>Total</td>
												<td className="text-end">{qty(skuTotalAchieved)}</td>
												<td className="text-end">{numberFormat(skuTotalAmount)}</td>
											</tr>
										</tfoot>
									</table>
								</div>
							</div>
							<div className="col-xl-2 col-md-4">
								<Tile title="Best Division For MCO" className="mis-dash-division">
									<div className="mis-dash-plate bg-light-primary text-primary">{reportData.bestDivisionForMco.name}</div>
									<div className="mis-dash-big">{qty(reportData.bestDivisionForMco.quantity)} {reportData.bestDivisionForMco.unit}</div>
								</Tile>
							</div>
							<div className="col-xl-5 col-md-8">
								<div className="mis-dash-tile" data-export-title="Best Seller (Multigrade)">
									<div className="mis-dash-tile-head mis-dash-head">Best Seller Nationally (Multigrade)</div>
									<table className="mis-dash-table">
										<thead>
											<tr className="mis-dash-head">
												<th>Name</th>
												<th className="text-end">Target</th>
												<th className="text-end">Achieved Target</th>
												<th className="text-end">Achieved Ltr. %</th>
											</tr>
										</thead>
										<tbody>
											{reportData.bestSellers.length === 0 ? (
												<tr>
													<td colSpan={4} className="text-center py-5">No seller data</td>
												</tr>
											) : (
												reportData.bestSellers.map((row) => (
													<tr key={row.name}>
														<td>{row.name}</td>
														<td className="text-end">{qty(row.target)}</td>
														<td className="text-end">{qty(row.achievedTarget)}</td>
														<td className="text-end">{percent(row.achievedLtrPercent)}</td>
													</tr>
												))
											)}
										</tbody>
									</table>
								</div>
							</div>
						</div>

						{/* Details per supervisor, free products, customers */}
						<div className="row g-1">
							<div className="col-xl-9">
								<div className="mis-dash-tile" data-export-title="Details">
									<table className="mis-dash-table">
										<thead>
											<tr className="mis-dash-head">
												<th>Details</th>
												<th className="text-end">Target Liter</th>
												<th className="text-end">Achieved Ltr.</th>
												<th className="text-end">Amount</th>
												<th className="text-end">Achieved Ltr. %</th>
											</tr>
										</thead>
										<tbody>
											{reportData.details.length === 0 ? (
												<tr>
													<td colSpan={5} className="text-center py-8">No details data</td>
												</tr>
											) : (
												reportData.details.map((row) => (
													<tr key={row.name}>
														<td>{row.name}</td>
														<td className="text-end">{qty(row.targetLiter)}</td>
														<td className="text-end">{qty(row.achievedLtr)}</td>
														<td className="text-end">{numberFormat(row.amount)}</td>
														<td className="text-end">{percent(row.achievedLtrPercent)}</td>
													</tr>
												))
											)}
										</tbody>
										<tfoot>
											<tr className="fw-bold bg-light-primary text-primary">
												<td>Total</td>
												<td className="text-end">{qty(detailsTotal.target)}</td>
												<td className="text-end">{qty(detailsTotal.achieved)}</td>
												<td className="text-end">{numberFormat(detailsTotal.amount)}</td>
												<td className="text-end">{detailsTotal.target > 0 ? qty(Math.round((detailsTotal.achieved / detailsTotal.target) * 10000) / 100) : '-'}</td>
											</tr>
										</tfoot>
									</table>
								</div>
							</div>
							<div className="col-xl-3 d-flex flex-column gap-1">
								<Tile title="Total Free Products" dark className="flex-fill">
									<div className="mis-dash-big">{qty(reportData.totalFreeProducts)}</div>
									<div className="mis-dash-unit">Ltr.</div>
								</Tile>
								<Tile title="Number Of Customers Trans." dark className="flex-fill">
									<div className="mis-dash-big">{qty(reportData.numberOfCustomersTrans)}</div>
								</Tile>
							</div>
						</div>
					</Card.Body>
				</Card>
			)}
		</>
	);
};

export default MisDashboardReport;
